import axios from 'axios'

const TOKEN_NAME_KEY = 'tokenName'
const REFRESH_TOKEN_KEY = 'refreshToken'
const CLIENT_TYPE_KEY = 'clientType'
const ACCESS_TOKEN_REFRESH_SKEW_SECONDS = 60

const clientConfigMap = {
    app_client: {
        clientId: 'app_client',
        clientSecret: 'dpl_client@123456',
        supportsRefresh: true
    },
    web_clazz_client: {
        clientId: 'web_clazz_client',
        clientSecret: 'dpl_web@123456',
        supportsRefresh: true
    },
    web_baby_client: {
        clientId: 'web_baby_client',
        clientSecret: 'dpl_web@123456',
        supportsRefresh: true
    },
    admin_client: {
        clientId: 'admin_client',
        clientSecret: 'dpl_admin@123456',
        supportsRefresh: false
    }
}

const refreshService = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 5000,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
    }
})

function decodeBase64Url(value) {
    if (!value) {
        return null
    }

    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
    const padding = normalized.length % 4
    const padded = padding ? normalized + '='.repeat(4 - padding) : normalized

    return atob(padded)
}

function parseJwtPayload(token = getAccessToken()) {
    if (!token) {
        return null
    }

    const tokenParts = token.split('.')
    if (tokenParts.length < 2) {
        return null
    }

    try {
        return JSON.parse(decodeBase64Url(tokenParts[1]))
    } catch (error) {
        return null
    }
}

function resolveAdditionalInfo(tokenData = {}) {
    return tokenData.additionalInformation || tokenData.additional_information || {}
}

function resolveRefreshTokenValue(refreshToken) {
    if (!refreshToken) {
        return null
    }

    if (typeof refreshToken === 'string') {
        return refreshToken
    }

    if (typeof refreshToken === 'object') {
        return refreshToken.value || refreshToken.token || null
    }

    return null
}

function inferClientTypeFromContext() {
    const payload = parseJwtPayload()
    const payloadClientType = payload?.client_id || payload?.clientId
    if (payloadClientType && clientConfigMap[payloadClientType]) {
        return payloadClientType
    }

    const tokenName = localStorage.getItem(TOKEN_NAME_KEY)
    if (tokenName === 'admin_token') {
        return 'admin_client'
    }

    if (tokenName === 'baby_token') {
        return 'web_baby_client'
    }

    if (tokenName === 'teacher_token') {
        return 'web_clazz_client'
    }

    return null
}

export function getTokenName() {
    return localStorage.getItem(TOKEN_NAME_KEY)
}

export function getAccessToken() {
    const tokenName = getTokenName()
    return tokenName ? localStorage.getItem(tokenName) : null
}

export function getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function getClientType() {
    const clientType = localStorage.getItem(CLIENT_TYPE_KEY)
    if (clientType) {
        return clientType
    }

    const inferredClientType = inferClientTypeFromContext()
    if (inferredClientType) {
        localStorage.setItem(CLIENT_TYPE_KEY, inferredClientType)
    }
    return inferredClientType
}

export function getTokenStorageName(clientType = getClientType()) {
  return clientType === 'admin_client' ? 'admin_token' : (clientType === 'web_clazz_client' ? 'teacher_token' : 'baby_token')
}

export function getRefreshClientConfig(clientType = getClientType()) {
    return clientType ? clientConfigMap[clientType] || null : null
}

export function canRefreshAccessToken() {
    const clientConfig = getRefreshClientConfig()
    return Boolean(getRefreshToken() && clientConfig?.supportsRefresh)
}

export function isAccessTokenExpired(token = getAccessToken(), skewSeconds = ACCESS_TOKEN_REFRESH_SKEW_SECONDS) {
    const payload = parseJwtPayload(token)
    if (!payload?.exp) {
        return false
    }

    return payload.exp * 1000 <= Date.now() + skewSeconds * 1000
}

export function saveAuthInfo(tokenData = {}) {
    const additionalInfo = resolveAdditionalInfo(tokenData)
    const previousClientType = getClientType()
    const clientType = tokenData.client_type
        || tokenData.clientType
        || additionalInfo.client_type
        || additionalInfo.clientType
        || previousClientType
    const tokenName = getTokenStorageName(clientType)
    const accessToken = tokenData.access_token || tokenData.accessToken || tokenData.value
    const refreshToken = resolveRefreshTokenValue(
        tokenData.refresh_token || tokenData.refreshToken || additionalInfo.refresh_token || additionalInfo.refreshToken
    )
    const shouldClearRefreshToken = !refreshToken && (
        Object.prototype.hasOwnProperty.call(tokenData, 'refresh_token')
        || Object.prototype.hasOwnProperty.call(tokenData, 'refreshToken')
        || Object.prototype.hasOwnProperty.call(tokenData, 'additionalInformation')
        || Object.prototype.hasOwnProperty.call(tokenData, 'additional_information')
        || (clientType && previousClientType && clientType !== previousClientType)
    )

    if (clientType) {
        localStorage.setItem(CLIENT_TYPE_KEY, clientType)
        localStorage.setItem(TOKEN_NAME_KEY, tokenName)
    }

    if (accessToken) {
        localStorage.setItem(tokenName, accessToken)
    }

    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    } else if (shouldClearRefreshToken) {
        localStorage.removeItem(REFRESH_TOKEN_KEY)
    }

    const subjectId = tokenData.subject_id || tokenData.subjectId || additionalInfo.subject_id || additionalInfo.subjectId
    if (subjectId) {
        localStorage.setItem('subjectId', subjectId)
    }

    const planId = tokenData.plan_id || tokenData.planId || additionalInfo.plan_id || additionalInfo.planId
    if (planId) {
        localStorage.setItem('planId', planId)
    }

    return {
        clientType,
        tokenName,
        accessToken: accessToken || getAccessToken(),
        refreshToken: refreshToken || getRefreshToken()
    }
}

export function clearAuthStorage() {
    const tokenName = getTokenName()
    if (tokenName) {
        localStorage.removeItem(tokenName)
    }
    localStorage.removeItem(TOKEN_NAME_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(CLIENT_TYPE_KEY)
    localStorage.removeItem('subjectId')
    localStorage.removeItem('clazzId')
    localStorage.removeItem('planId')
}

export async function refreshAccessToken() {
    const refreshToken = getRefreshToken()
    const clientType = getClientType()
    const clientConfig = getRefreshClientConfig(clientType)

    if (!refreshToken || !clientConfig?.supportsRefresh) {
        throw new Error('No refresh token available')
    }

    const formData = new URLSearchParams()
    formData.append('grant_type', 'refresh_token')
    formData.append('refresh_token', refreshToken)
    formData.append('client_id', clientConfig.clientId)
    formData.append('client_secret', clientConfig.clientSecret)

    try {
        const response = await refreshService.post('/oauth/token', formData)
        const tokenData = response.data?.data || response.data

        if (!(tokenData?.access_token || tokenData?.accessToken || tokenData?.value)) {
            throw new Error('Refresh token response is invalid')
        }

        return saveAuthInfo({
            ...tokenData,
            client_type: tokenData.client_type || clientType
        })
    } catch (error) {
        console.info(error)
        const status = error?.response?.status
        if (status === 400 || status === 401) {
            clearAuthStorage()
        }
        throw error
    }
}
